import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Alert,
  Button,
  message,
  Modal as ModalAntD,
  Spin,
  Switch,
} from 'antd';
import { Formik, useFormikContext } from 'formik';
import { T, TF } from 'react-translator-component';

import {
  copyFromConfig,
  DoorFormValues,
  openingLine,
  summaryFromConfig,
  toFormValues,
  toPayload,
} from './doorAccessMapping';
import DoorAttemptBox, { DoorAttempt } from './DoorAttemptBox';
import {
  DoorFormErrors,
  hasDoorErrors,
  validateDoorForm,
} from './doorValidation';
import RequestBuilder from './RequestBuilder';
import {
  DoorActions,
  EnableRow,
  HistoryList,
  LinkButton,
} from './styled';

import { doorAccessApi, doorErrorMessage } from '../../../../hooks/doorAccess';
import {
  DoorConfig,
  DoorOpening,
  DoorSummary,
} from '../../../../types/responses/DoorAccess';

const Modal: any = ModalAntD;

export interface DoorCopySource {
  connectorId: number;
  label: string;
}

// Registrado no StationDoorAccessForm: o "Enviar" da estação valida e salva por aqui.
export interface DoorPanelApi {
  isDirty: () => boolean;
  validate: () => Promise<boolean>;
  // null quando salvou; a mensagem de erro quando não salvou
  save: () => Promise<string | null>;
}

interface DoorConnectorPanelProps {
  chargeBoxPk: string | number;
  connectorId: number;
  copySources: DoorCopySource[];
  register: (connectorId: number, api: DoorPanelApi) => () => void;
  onSaved: (summary: DoorSummary) => void;
}

type BusyAction = 'test' | 'open' | 'copy';

interface PanelBodyProps extends DoorConnectorPanelProps {
  onConfigChange: (config: DoorConfig) => void;
}

const PanelBody = ({
  chargeBoxPk,
  connectorId,
  copySources,
  register,
  onSaved,
  onConfigChange,
}: PanelBodyProps) => {
  const formik = useFormikContext<DoorFormValues>();
  const { values, dirty, setFieldValue, setValues } = formik;
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState<BusyAction | null>(null);
  const busyRef = useRef(false);
  const [attempt, setAttempt] = useState<DoorAttempt | null>(null);
  const mounted = useRef(true);
  const formikRef = useRef(formik);
  formikRef.current = formik;

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const begin = (action: BusyAction): boolean => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusy(action);
    return true;
  };

  const end = () => {
    busyRef.current = false;
    if (mounted.current) setBusy(null);
  };

  const showAttempt = (next: DoorAttempt) => {
    if (mounted.current) setAttempt(next);
  };

  const copySource = copySources[0];

  const ensureValid = async (): Promise<boolean> => {
    const found = (await formikRef.current.validateForm()) as DoorFormErrors;
    if (hasDoorErrors(found)) {
      if (mounted.current) setShowErrors(true);
      return false;
    }
    return true;
  };

  useEffect(
    () =>
      register(connectorId, {
        isDirty: () => formikRef.current.dirty,
        validate: ensureValid,
        save: async () => {
          const current = formikRef.current;
          let saved: DoorConfig | undefined;
          try {
            saved = await doorAccessApi.saveConfig(
              chargeBoxPk,
              connectorId,
              toPayload(current.values),
            );
          } catch (error) {
            return doorErrorMessage(error);
          }
          try {
            const fresh =
              saved ??
              (await doorAccessApi.getConfig(chargeBoxPk, connectorId));
            onSaved(summaryFromConfig(connectorId, fresh));
            if (mounted.current) {
              setShowErrors(false);
              onConfigChange(fresh);
              current.resetForm({ values: toFormValues(fresh) });
            }
          } catch (error) {
            // salvou; só não deu para recarregar o status
          }
          return null;
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [register, chargeBoxPk, connectorId, onSaved, onConfigChange],
  );

  const [openings, setOpenings] = useState<DoorOpening[] | null>(null);
  const [openingsError, setOpeningsError] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const openingsRequest = useRef(0);

  const loadOpenings = useCallback(async (): Promise<boolean> => {
    openingsRequest.current += 1;
    const requestId = openingsRequest.current;
    try {
      const list = await doorAccessApi.getOpenings(chargeBoxPk, connectorId);
      if (mounted.current && requestId === openingsRequest.current) {
        setOpenings(list);
        setOpeningsError(false);
      }
      return true;
    } catch (error) {
      if (requestId !== openingsRequest.current) return true;
      if (mounted.current) setOpeningsError(true);
      return false;
    }
  }, [chargeBoxPk, connectorId]);

  useEffect(() => {
    loadOpenings();
  }, [loadOpenings]);

  const runTest = async () => {
    if (!begin('test')) return;
    if (!(await ensureValid())) {
      message.error(T('Check the highlighted fields.'));
      end();
      return;
    }
    try {
      const result = await doorAccessApi.testConfig(
        chargeBoxPk,
        connectorId,
        toPayload(values),
      );
      showAttempt({ kind: 'test', result });
    } catch (error) {
      showAttempt({ kind: 'test', error: doorErrorMessage(error) });
    } finally {
      end();
    }
  };

  const openNow = async () => {
    if (busyRef.current) return;
    // Com alteração pendente a abertura vai com a config da tela, sem salvar (teste antes de
    // salvar); sem alteração, o CS usa a config salva.
    if (dirty && !(await ensureValid())) {
      message.error(T('Check the highlighted fields.'));
      return;
    }
    const unsaved = dirty ? toPayload(values) : undefined;
    Modal.confirm({
      title: T('Open the door now?'),
      content: unsaved
        ? `${TF('This will unlock the access door of this connector.')} ${TF(
            'The settings on screen will be used for this opening, without saving.',
          )}`
        : T('This will unlock the access door of this connector.'),
      okText: T('Open door'),
      cancelText: T('Cancel'),
      onOk: async () => {
        if (!begin('open')) return;
        try {
          const result = await doorAccessApi.open(
            chargeBoxPk,
            connectorId,
            unsaved,
          );
          showAttempt({ kind: 'open', result });
        } catch (error) {
          showAttempt({ kind: 'open', error: doorErrorMessage(error) });
          end();
          return;
        }
        const refreshed = await loadOpenings();
        if (!refreshed) {
          message.warning(
            T(
              'The door was triggered, but the last opening could not be refreshed.',
            ),
          );
        }
        end();
      },
    });
  };

  const copyFrom = async (sourceId: number) => {
    if (!begin('copy')) return;
    try {
      const source = await doorAccessApi.getConfig(chargeBoxPk, sourceId);
      if (mounted.current) {
        setValues(copyFromConfig(source));
        setShowErrors(false);
      }
    } catch (error) {
      message.error(doorErrorMessage(error));
    } finally {
      end();
    }
  };

  const lastOpening = () => {
    if (openingsError) {
      return (
        <span className='empty'>
          {T('Could not load the opening history.')}
        </span>
      );
    }
    if (!openings) return <Spin size='small' />;
    if (openings.length === 0) {
      return <span className='empty'>{T('No opening recorded.')}</span>;
    }
    return (
      <>
        {openingLine(openings[0])}
        {openings.length > 1 && (
          <LinkButton
            type='button'
            style={{ marginLeft: 12 }}
            onClick={() => setShowHistory(true)}
          >
            {T('Last 10 openings')}
          </LinkButton>
        )}
      </>
    );
  };

  return (
    <>
      <EnableRow>
        <Switch
          checked={values.enabled}
          onChange={(checked) => setFieldValue('enabled', checked)}
        />
        <div className='text'>
          <span className='label'>{T('This connector has door unlock')}</span>
          <span className='sub'>
            {T(
              'When enabled, enables remote opening settings and the unlock button in the driver app',
            )}
          </span>
        </div>
        {copySource && (
          <LinkButton
            type='button'
            disabled={busy === 'copy'}
            onClick={() => copyFrom(copySource.connectorId)}
          >
            {TF('Copy from connector {0}', copySource.connectorId)}
          </LinkButton>
        )}
      </EnableRow>

      {values.enabled && (
        <>
          <RequestBuilder showErrors={showErrors} />

          <DoorActions>
            <div className='info'>
              <div>
                <span className='k'>{T('Last opening')}:</span>
                {lastOpening()}
              </div>
              <DoorAttemptBox attempt={attempt} />
            </div>
            <div className='buttons'>
              <Button loading={busy === 'test'} onClick={runTest}>
                {T('Test connection')}
              </Button>
              <Button loading={busy === 'open'} onClick={openNow}>
                {T('Open door now')}
              </Button>
            </div>
          </DoorActions>
        </>
      )}

      <Modal
        visible={showHistory}
        title={T('Last 10 openings')}
        footer={null}
        onCancel={() => setShowHistory(false)}
      >
        <HistoryList>
          {(openings ?? []).map((opening, index) => (
            <li key={`${opening.at}-${index}`}>{openingLine(opening)}</li>
          ))}
        </HistoryList>
      </Modal>
    </>
  );
};

const DoorConnectorPanel = (props: DoorConnectorPanelProps) => {
  const { chargeBoxPk, connectorId } = props;
  const [config, setConfig] = useState<DoorConfig | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const fresh = await doorAccessApi.getConfig(chargeBoxPk, connectorId);
      if (mounted.current) setConfig(fresh);
    } catch (error) {
      if (mounted.current) setLoadError(doorErrorMessage(error));
    }
  }, [chargeBoxPk, connectorId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loadError) {
    return (
      <Alert
        type='error'
        showIcon
        message={T('Could not load the door settings.')}
        description={loadError}
        action={
          <Button size='small' onClick={load}>
            {T('Try again')}
          </Button>
        }
      />
    );
  }

  if (!config) return <Spin />;

  return (
    <Formik
      initialValues={toFormValues(config)}
      validate={validateDoorForm}
      onSubmit={() => undefined}
    >
      <PanelBody {...props} onConfigChange={setConfig} />
    </Formik>
  );
};

export default DoorConnectorPanel;
